"use client";
// Inline waitlist sign-up: a button that opens a small popup where the visitor
// drops their email and submits — no separate page navigation.
import { useState, useTransition } from "react";
import { useTranslations, useLocale } from "next-intl";
import { joinWaitlist } from "@/actions/course-waitlist";
import { translateServerError } from "@/lib/courses/translate-server-error";

export function WaitlistButton({ slug, label, className }: { slug: string; label: string; className?: string }) {
  const locale = useLocale();
  const tf = useTranslations("public.courses.form");
  const tw = useTranslations("public.courses.waitlist");
  const te = useTranslations("public.courses.errors");
  const [open, setOpen] = useState(false);
  const [pending, start] = useTransition();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  function close() { if (!pending) { setOpen(false); setError(null); } }
  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    start(async () => {
      const r = await joinWaitlist(slug, { full_name: "", email, phone: "" });
      if (!r.success) { setError(translateServerError(r.error, te)); return; }
      setDone(true);
    });
  }

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={className}>{label}</button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" onClick={close}>
          <div className="w-full max-w-sm rounded-xl border border-(--fm-border) bg-(--fm-surface) p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-start justify-between gap-3">
              <h2 className="text-lg font-semibold">{tw("joinCta")}</h2>
              <button type="button" aria-label={locale === "ar" ? "إغلاق" : "Close"} onClick={close} className="text-(--fm-text-secondary) hover:text-(--fm-text-primary)">✕</button>
            </div>
            {done ? (
              <div className="text-center">
                <p className="font-medium">{tw("doneTitle")}</p>
                <p className="mt-1 text-sm text-(--fm-text-secondary)">{tw("doneBody")}</p>
                <button type="button" onClick={() => setOpen(false)} className="mt-4 rounded bg-(--fm-accent) px-4 py-2 text-sm text-(--fm-background)">
                  {locale === "ar" ? "حسناً" : "OK"}
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                {error && <p className="rounded bg-red-100 px-3 py-2 text-sm text-red-800">{error}</p>}
                <label className="block space-y-1" dir="auto"><span className="text-sm font-medium">{tf("emailLabel")} *</span>
                  <input required type="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
                    className="w-full rounded border border-(--fm-border) bg-transparent px-3 py-2 text-sm" /></label>
                <button type="submit" disabled={pending} className="w-full rounded bg-(--fm-accent) px-4 py-2 text-sm text-(--fm-background) disabled:opacity-60">
                  {pending ? tw("joinPending") : tw("joinCta")}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
